/**
 * nativePush.js
 * Handles push notifications for Capacitor native apps (iOS + Android).
 * For web, the existing ServiceWorker / PushManager path in dbService.js is used.
 *
 * Flow:
 *   1. requestPermission()   — ask OS for notification permission
 *   2. PushNotifications.register()  — get FCM (Android) / APNs (iOS) token
 *   3. Send token to server via POST /api/push/native-token
 *   4. Server uses FCM/APNs to deliver pushes to the device
 *
 * Required: @capacitor/push-notifications is already installed.
 */

import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';

/** True when running inside a real native app (not the browser PWA). */
export const isNative = () => Capacitor.isNativePlatform();

const BASE = process.env.REACT_APP_API_URL || '/api';

/** One-time setup — call from App.js after the user is authenticated. */
export async function setupNativePush(userId) {
  if (!isNative()) return; // web handles its own push via SW

  // Request OS-level permission (shows the native dialog on first run)
  const result = await PushNotifications.requestPermissions();
  if (result.receive !== 'granted') return;

  // Register with FCM / APNs
  await PushNotifications.register();

  // Token received → send to server
  PushNotifications.addListener('registration', async (token) => {
    try {
      await fetch(`${BASE}/push/native-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          token: token.value,
          platform: Capacitor.getPlatform(), // 'android' | 'ios'
        }),
      });
    } catch { /* ignore network errors */ }
  });

  // Token error — silent fail
  PushNotifications.addListener('registrationError', () => {});

  // Foreground notification — Capacitor shows a native banner automatically
  // (controlled by the presentationOptions in capacitor.config.ts)
  PushNotifications.addListener('pushNotificationReceived', () => {});

  // User tapped a notification
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const url = action.notification?.data?.url;
    if (url) {
      // Navigate within the SPA by dispatching a custom event
      window.dispatchEvent(new CustomEvent('sl-navigate', { detail: { url } }));
    }
  });
}

/** Remove all listeners — call on logout. */
export async function teardownNativePush() {
  if (!isNative()) return;
  await PushNotifications.removeAllListeners();
}
